import {
    useGetHealthQuestionnaireQuery,
    useLazyGetHealthQuestionnaireQuery,
    useSubmitHealthQuestionnaireMutation,
} from '../services/questionnaires/questionnairesApi.slice';

import type { SubmitHealthQuestionnaireRequest } from '@/rtkQuery/features/models/questionnaires.model';
import { toastService } from '@/utils/toastService';

export const useHealthQuestionnaireService = () => {
    // Eager query
    const {
        data,
        error,
        isLoading,
        isError,
        isSuccess,
        refetch,
        status,
    } = useGetHealthQuestionnaireQuery();

    // Lazy query
    const [
        trigger,
        {
            data: lazyData,
            error: lazyError,
            isLoading: lazyLoading,
            isSuccess: lazySuccess,
            isError: lazyFail,
            status: lazyStatus,
        },
    ] = useLazyGetHealthQuestionnaireQuery();

    // Submit mutation
    const [
        triggerSubmit,
        {
            data: submitData,
            isLoading: isSubmitting,
            isError: isSubmitError,
            isSuccess: isSubmitSuccess,
            error: submitError,
            status: submitStatus,
        },
    ] = useSubmitHealthQuestionnaireMutation();

    const submitHealthQuestionnaire = async (payload: SubmitHealthQuestionnaireRequest) => {
        try {
            const response = await triggerSubmit(payload).unwrap();
            toastService.success('Health questionnaire submitted successfully.');
            return response;
        } catch (err: any) {
            const message =
                err?.data?.message || err?.error || 'Failed to submit health questionnaire.';
            toastService.error(message);
            throw err;
        }
    };

    const errorMessage =
        (error as any)?.meta?.error || (error as any)?.data?.message || 'Something went wrong';

    const lazyErrorMessage =
        (lazyError as any)?.meta?.error || (lazyError as any)?.data?.message || 'Something went wrong';

    const submitErrorMessage =
        (submitError as any)?.meta?.error || (submitError as any)?.data?.message || 'Submission failed';

    return {
        // Eager query
        healthQuestionnaireData: data,
        healthQuestionnaireIsLoading: isLoading,
        healthQuestionnaireIsError: isError,
        healthQuestionnaireIsSuccess: isSuccess,
        healthQuestionnaireError: error,
        healthQuestionnaireErrorMessage: errorMessage,
        healthQuestionnaireRefetch: refetch,
        healthQuestionnaireStatus: status,

        // Lazy query
        fetchHealthQuestionnaire: trigger,
        healthQuestionnaireLazyData: lazyData,
        healthQuestionnaireLazyError: lazyError,
        healthQuestionnaireLazyIsLoading: lazyLoading,
        healthQuestionnaireLazyIsSuccess: lazySuccess,
        healthQuestionnaireLazyIsError: lazyFail,
        healthQuestionnaireLazyStatus: lazyStatus,
        healthQuestionnaireLazyErrorMessage: lazyErrorMessage,

        // Submission mutation
        submitHealthQuestionnaire,
        healthQuestionnaireSubmitData: submitData,
        healthQuestionnaireIsSubmitting: isSubmitting,
        healthQuestionnaireIsSubmitError: isSubmitError,
        healthQuestionnaireIsSubmitSuccess: isSubmitSuccess,
        healthQuestionnaireSubmitError: submitError,
        healthQuestionnaireSubmitErrorMessage: submitErrorMessage,
        healthQuestionnaireSubmitStatus: submitStatus,
    };
};
